import { useEffect, useState, useCallback } from "react";
import BackImg from "@assets/images/stepsBack.jpg";
import { Avatar, Card, CircularProgress } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { fetchAllPlayers } from "api/quizGame";
import {
  YellowRanks,
  GreenRank,
  GrayRank,
  BrownRanks,
} from "@utils/defaultImages";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

const WeeklyLeaderBoard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const {t} = useTranslation();
  const Ranks = [<YellowRanks />, <GrayRank />, <BrownRanks />, <GreenRank />];
  const navigate = useNavigate();

  const fetchLeaderboardData = useCallback(
    async (page = 1, isNewLoad = false) => {
      try {
        const response = await fetchAllPlayers({ page });

        if (response) {
          // Extract data and pagination info from response
          const {
            data: players,
            current_page,
            last_page,
            total,
          } = response?.top_players_this_week;

          if (isNewLoad || page === 1) {
            setLeaderboardData(players);
          } else {
            setLeaderboardData((prev) => [...prev, ...players]);
          }

          // Update pagination state
          setHasMore(current_page < last_page);
          setCurrentPage(current_page + 1);
        }

        setIsInitialLoad(false);
      } catch (error) {
        console.error("Failed to get leaderboard data:", error);
        setError(error.response?.data?.message || "An error occurred");
        setIsInitialLoad(false);
      }
    },
    []
  );

  // Initial fetch
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setLeaderboardData([]);
    setIsInitialLoad(true);
    fetchLeaderboardData(1, true);
  }, [fetchLeaderboardData]);

  // Function called when user scrolls to bottom
  const fetchMoreData = () => {
    if (hasMore) {
      fetchLeaderboardData(currentPage);
    }
  };

  return (
    <>
      <Card className="p-5 !rounded-xl">
        <div className="flex justify-end">
          <button
            className="bg-[#64DDFF] py-[12px] px-6 text-white rounded-full  mb-2 flex items-center gap-2"
            onClick={() => navigate("/quiz-game")}
          >
            <ChevronLeft
              className="w-6 h-6 bg-white rounded-full text-[#4CDBFF] p-[2px]"
              strokeWidth={4}
            />
            {t("quizGame.Return To Dashboard")}
          </button>
        </div>
        <div
          className="p-4 md:p-6 rounded-lg bg-cover from-blue-600 to-blue-500 h-full"
          style={{ backgroundImage: `url(${BackImg})` }}
        >
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-[14px] md:text-[16px] font-[Lato] text-white">
              {t("quizGame.Weekly Leaderboard")}
            </h4>
          </div>

          {/* Initial loading state */}
          {isInitialLoad ? (
            <div className="bg-white rounded-[16px] p-8 text-center">
              <CircularProgress color="primary" thickness={7} />
            </div>
          ) : leaderboardData?.length > 0 ? (
            <div className="bg-white rounded-[16px] overflow-hidden">
              {/* Header row - Fixed grid structure */}
              <div className="sticky top-0 bg-white z-10 rounded-t-[16px] pt-4 px-2 md:px-4 grid grid-cols-12 gap-1 items-center pb-2">
                <div className="col-span-6 md:col-span-7 text-[10px] md:text-[12px] text-[#767676] font-[Lato] flex items-center pl-2">
                  {t("quizGame.Player")}
                </div>
                <div className="col-span-3 md:col-span-2 text-[10px] md:text-[12px] text-[#767676] font-[Lato] text-center">
                  {t("quizGame.Points")}
                </div>
                <div className="col-span-3 md:col-span-3 text-[10px] md:text-[12px] text-[#767676] font-[Lato] text-center">
                  {t("quizGame.Ranks")}
                </div>
              </div>

              {/* Scrollable data container */}
              <div
                id="scrollable-leaderboard-container"
                className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar"
              >
                <InfiniteScroll
                  dataLength={leaderboardData?.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={
                    <div className="text-center py-4">
                      <CircularProgress
                        color="primary"
                        thickness={7}
                        size={24}
                      />
                    </div>
                  }
                  // endMessage={
                  //   <div className="text-gray-500 text-center py-2 text-sm">
                  //     All players loaded
                  //   </div>
                  // }
                  scrollableTarget="scrollable-leaderboard-container"
                >
                  <div className="px-2 md:px-4 space-y-2 pb-4">
                    {leaderboardData?.map((player, index) => {
                      // Calculate global rank based on current page and index
                      // If your API returns a rank field, use that instead
                      const globalRank = (currentPage - 1) * 10 + index + 1;

                      return (
                        <div
                          key={player.id || index}
                          className={`grid grid-cols-12 gap-1 items-center rounded-xl py-2 px-2 ${
                            player.isCurrentUser || player.is_current_user
                              ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] !text-white"
                              : index % 2 === 0
                              ? "bg-gray-50"
                              : "bg-white"
                          }`}
                        >
                          {/* Player column - Same grid structure as header */}
                          <div className="col-span-6 md:col-span-7 flex items-center">
                            <Avatar
                              className="!w-5 !h-5 md:!w-6 md:!h-6 mr-1 md:mr-2"
                              src={player.avatar || player.profile_url}
                              alt={player.name}
                            />
                            <span className="text-[12px] md:text-[14px] font-[Lato] truncate">
                              {player.name || "Unknown Player"}
                            </span>
                          </div>

                          {/* Points column - Same grid structure as header */}
                          <div className="col-span-3 md:col-span-2 text-center">
                            <span className="text-[12px] md:text-[14px] font-[Lato] font-normal pl-2">
                              {(player.points || 0).toLocaleString()}
                            </span>
                          </div>

                          {/* Rank column - Same grid structure as header */}
                          <div className="col-span-3 md:col-span-3 flex justify-center pl-5">
                            <div className="relative">
                              {Ranks[Math.min(index, 3)]}
                              <div className="absolute top-2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[14px] font-[Lato] text-white">
                                {player?.rank || globalRank}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </InfiniteScroll>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-[16px] p-8 text-center text-gray-500">
              No leaderboard data available
            </div>
          )}

          {/* Error message */}
          {/* {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
          {error}
        </div>
      )} */}

          <style jsx>{`
            .custom-scrollbar::-webkit-scrollbar {
              width: 8px;
            }

            .custom-scrollbar::-webkit-scrollbar-track {
              background-color: #f1f1f1;
              border-radius: 10px;
              margin: 4px 0;
            }

            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: linear-gradient(
                150deg,
                #0771ef 12.89%,
                #15aafc 85.22%
              );
              border-radius: 10px;
              border: 1px solid rgba(255, 255, 255, 0.2);
            }

            @media (max-width: 640px) {
              .custom-scrollbar::-webkit-scrollbar {
                width: 4px;
              }
            }
          `}</style>
        </div>
      </Card>
    </>
  );
};

export default WeeklyLeaderBoard;
